import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Global storefront filter:
 *   - `query`: free-text search (driven by Header search input)
 *   - `category`: a normalized tag like "home" | "b2c" | "b2b" | "white-label" |
 *                 "line-access" | "platforms" | "join" | "business" — driven by
 *                 the top Navigation strip / MobileMenu.
 *
 * Component cards consume `matches(card)` to decide whether to render. A card
 * matches if BOTH conditions hold:
 *    1. category is "home" OR the item declares a tag-set that includes
 *       category (or its label includes it as a substring).
 *    2. query is empty OR the item's name/label/sub/tagline contains the query.
 */

const FilterContext = createContext(null);

function tagsOf(item) {
  if (!item) return '';
  const raw = [
    item.id, item.label, item.name, item.sub, item.subtitle, item.title,
    item.description, item.tagline, item.category, item.kind, item.type,
    item.rate, item.link, item.visitUrl,
    ...(Array.isArray(item.tags) ? item.tags : [item.tags])
  ].filter(Boolean).join(' ').toLowerCase();
  return raw;
}

export function FilterProvider({ children }) {
  const [query, setQueryRaw] = useState('');
  const [category, setCategoryRaw] = useState('home');

  const setQuery = useCallback((q) => setQueryRaw((q || '').toString()), []);
  const setCategory = useCallback((c) => setCategoryRaw((c || 'home').toString().toLowerCase()), []);

  const matches = useCallback((item) => {
    const text = tagsOf(item);
    const q = query.trim().toLowerCase();
    if (q && !text.includes(q)) return false;
    if (category === 'home' || !category) return true;
    // Build a list of alias tokens to try — handles ids like b2cwl/b2cui where
    // the underlying card labels say "B2C White Label" / "B2C User ID".
    const cat = category.replace(/[^a-z0-9]/g, '');
    const norm = text.replace(/[^a-z0-9]/g, '');
    const aliases = new Set([cat]);
    // Strip common suffixes (wl, ui, id) — e.g. b2cwl → b2c
    const stripped = cat.replace(/(wl|ui|id|api)$/, '');
    if (stripped && stripped !== cat) aliases.add(stripped);
    // Also try the leading 2-3 token cluster (e.g. lineaccess → line)
    const prefix = cat.match(/^([a-z]+\d*)/);
    if (prefix && prefix[1]) aliases.add(prefix[1]);
    // Match if ANY alias is found in the normalised text
    for (const a of aliases) { if (a && norm.includes(a)) return true; }
    return false;
  }, [query, category]);

  const value = useMemo(() => ({
    query, setQuery, category, setCategory, matches, tagsOf,
    active: !!query.trim() || (category && category !== 'home'),
    reset: () => { setQueryRaw(''); setCategoryRaw('home'); },
  }), [query, category, matches, setQuery, setCategory]);

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

export function useFilter() {
  const ctx = useContext(FilterContext);
  if (!ctx) {
    // graceful fallback so components don't crash if provider was forgotten
    return { query: '', setQuery: () => {}, category: 'home', setCategory: () => {}, matches: () => true, active: false, reset: () => {} };
  }
  return ctx;
}
